"use client";

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  User,
  Bot,
  Shield,
  Eye,
  Zap,
  Database,
  Settings,
  ChevronRight,
  Plus,
  Search,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Brain,
  Users,
  Globe,
  Server,
  FileText,
  Lock,
} from "lucide-react";
import type {
  AgentProfile,
  AgentDomain,
  AgentKind,
  AgentProviderType,
  AgentRegistrationStatus,
  AgentRole,
  AllowedSurface,
  MemoryScope,
} from "@/lib/aura/types";
import type { AdminAgentCreateInput } from "@/src/data/admin-console-api";

// ============================================
// Props Interface
// ============================================

interface AgentStudioProps {
  agents: AgentProfile[];
  selectedAgentId: string | null;
  filterStatus: AgentRegistrationStatus | "all";
  filterKind: AgentKind | "all";
  onSelectAgent: (agentId: string | null) => void;
  onFilterStatus: (status: AgentRegistrationStatus | "all") => void;
  onFilterKind: (kind: AgentKind | "all") => void;
  onCreateAgent?: (request: AdminAgentCreateInput) => Promise<void> | void;
  onRegisterAgent?: (agentId: string) => void;
  onOpenInCommandAura?: (agentId: string) => void;
  onInviteToDecisionRoom?: (agentId: string) => void;
}

const inputClass =
  "h-9 w-full rounded-md border border-border/60 bg-muted/20 px-3 text-sm text-foreground outline-none focus:border-accent/60";
const textareaClass =
  "min-h-20 w-full rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-foreground outline-none focus:border-accent/60";

// ============================================
// Helper Functions
// ============================================

function getKindIcon(kind: AgentKind) {
  switch (kind) {
    case "ai":
      return Bot;
    case "human":
      return User;
    case "external-consultant":
      return Globe;
    case "internal-worker":
      return Users;
    case "auditor":
      return Shield;
    case "connector-operator":
      return Server;
    case "domain-expert":
      return Brain;
    default:
      return User;
  }
}

function getKindLabel(kind: AgentKind): string {
  switch (kind) {
    case "ai":
      return "AI Agent";
    case "human":
      return "Human";
    case "external-consultant":
      return "External Consultant";
    case "internal-worker":
      return "Internal Worker";
    case "auditor":
      return "Auditor";
    case "connector-operator":
      return "Connector Operator";
    case "domain-expert":
      return "Domain Expert";
    default:
      return kind;
  }
}

function getRoleLabel(role: AgentRole): string {
  switch (role) {
    case "executor":
      return "Executor";
    case "reviewer":
      return "Reviewer";
    case "auditor":
      return "Auditor";
    case "advisor":
      return "Advisor";
    case "supervisor":
      return "Supervisor";
    case "connector-operator":
      return "Connector Operator";
    case "memory-curator":
      return "Memory Curator";
    case "domain-expert":
      return "Domain Expert";
    default:
      return role;
  }
}

function getStatusBadge(status: AgentRegistrationStatus) {
  switch (status) {
    case "live":
      return {
        label: "Live",
        className: "bg-success/20 text-success border-success/30",
        icon: CheckCircle2,
      };
    case "draft":
      return {
        label: "Draft",
        className: "bg-muted/30 text-muted-foreground border-border/50",
        icon: FileText,
      };
    case "pending-core-registration":
      return {
        label: "Pending Core",
        className: "bg-warning/20 text-warning border-warning/30",
        icon: Clock,
      };
    case "unavailable":
      return {
        label: "Unavailable",
        className: "bg-destructive/20 text-destructive border-destructive/30",
        icon: XCircle,
      };
    case "requires-core-handler":
      return {
        label: "Requires Handler",
        className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
        icon: AlertTriangle,
      };
    default:
      return {
        label: status,
        className: "bg-muted/30 text-muted-foreground border-border/50",
        icon: FileText,
      };
  }
}

function getSurfaceLabel(surface: AllowedSurface): string {
  switch (surface) {
    case "command-aura":
      return "Command AURA";
    case "decision-room":
      return "Decision Room";
    case "founder-supervisor":
      return "Founder Supervisor";
    case "tools":
      return "Tools";
    case "sap-pack":
      return "SAP Pack";
    default:
      return surface;
  }
}

function getMemoryScopeLabel(scope: MemoryScope): string {
  switch (scope) {
    case "none":
      return "None";
    case "session":
      return "Session";
    case "workspace":
      return "Workspace";
    case "tenant":
      return "Tenant";
    case "domain-pack":
      return "Domain Pack";
    case "canonical-review-required":
      return "Canonical (Review Required)";
    default:
      return scope;
  }
}

// ============================================
// Filter Tabs Component
// ============================================

interface FilterTabsProps {
  filterStatus: AgentRegistrationStatus | "all";
  filterKind: AgentKind | "all";
  onFilterStatus: (status: AgentRegistrationStatus | "all") => void;
  onFilterKind: (kind: AgentKind | "all") => void;
  agents: AgentProfile[];
}

function FilterTabs({
  filterStatus,
  filterKind,
  onFilterStatus,
  onFilterKind,
  agents,
}: FilterTabsProps) {
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: agents.length };
    agents.forEach((a) => {
      counts[a.registrationStatus] = (counts[a.registrationStatus] || 0) + 1;
    });
    return counts;
  }, [agents]);

  const kindCounts = useMemo(() => {
    const counts: Record<string, number> = { all: agents.length };
    agents.forEach((a) => {
      counts[a.identity.kind] = (counts[a.identity.kind] || 0) + 1;
    });
    return counts;
  }, [agents]);

  const statusFilters: { value: AgentRegistrationStatus | "all"; label: string }[] = [
    { value: "all", label: "All" },
    { value: "live", label: "Live" },
    { value: "draft", label: "Draft" },
    { value: "pending-core-registration", label: "Pending" },
    { value: "unavailable", label: "Unavailable" },
    { value: "requires-core-handler", label: "Requires Handler" },
  ];

  const kindFilters: { value: AgentKind | "all"; label: string }[] = [
    { value: "all", label: "All Types" },
    { value: "ai", label: "AI" },
    { value: "human", label: "Human" },
    { value: "auditor", label: "Auditor" },
    { value: "domain-expert", label: "Expert" },
  ];

  return (
    <div className="space-y-3">
      {/* Status Filters */}
      <div className="flex flex-wrap gap-1.5">
        {statusFilters.map((filter) => (
          <button
            key={filter.value}
            onClick={() => onFilterStatus(filter.value)}
            className={cn(
              "text-[10px] px-2.5 py-1.5 rounded border transition-all cursor-pointer",
              filterStatus === filter.value
                ? "bg-accent text-background border-accent font-medium"
                : "bg-muted/20 text-foreground/80 border-border hover:bg-muted/40 hover:border-border/80"
            )}
          >
            {filter.label}
            {statusCounts[filter.value] !== undefined && (
              <span className="ml-1 opacity-70">({statusCounts[filter.value]})</span>
            )}
          </button>
        ))}
      </div>

      {/* Kind Filters */}
      <div className="flex flex-wrap gap-1.5">
        {kindFilters.map((filter) => (
          <button
            key={filter.value}
            onClick={() => onFilterKind(filter.value)}
            className={cn(
              "text-[10px] px-2.5 py-1.5 rounded border transition-all cursor-pointer",
              filterKind === filter.value
                ? "bg-accent text-background border-accent font-medium"
                : "bg-muted/20 text-foreground/80 border-border hover:bg-muted/40 hover:border-border/80"
            )}
          >
            {filter.label}
            {kindCounts[filter.value] !== undefined && (
              <span className="ml-1 opacity-70">({kindCounts[filter.value]})</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Agent List Item Component
// ============================================

interface AgentListItemProps {
  agent: AgentProfile;
  isSelected: boolean;
  onSelect: () => void;
}

function AgentListItem({ agent, isSelected, onSelect }: AgentListItemProps) {
  const KindIcon = getKindIcon(agent.identity.kind);
  const statusBadge = getStatusBadge(agent.registrationStatus);
  const StatusIcon = statusBadge.icon;

  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full text-left p-3 rounded-lg border transition-all cursor-pointer",
        isSelected
          ? "bg-accent/20 border-accent/50 shadow-sm"
          : "bg-muted/10 border-border/50 hover:bg-muted/30 hover:border-border hover:shadow-sm"
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
            agent.registrationStatus === "live"
              ? "bg-accent/20 text-accent"
              : "bg-muted/30 text-muted-foreground"
          )}
        >
          <KindIcon className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-foreground truncate">
              {agent.identity.displayName}
            </h4>
            <span
              className={cn(
                "flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border shrink-0",
                statusBadge.className
              )}
            >
              <StatusIcon className="w-2.5 h-2.5" />
              {statusBadge.label}
            </span>
          </div>

          <p className="text-xs text-muted-foreground mt-0.5">
            {getKindLabel(agent.identity.kind)} · {getRoleLabel(agent.capability.role)}
          </p>

          <p className="text-[10px] text-muted-foreground/70 mt-1 capitalize">
            {agent.identity.domain.replace("-", " ")}
          </p>
        </div>

        <ChevronRight
          className={cn(
            "w-4 h-4 shrink-0 transition-transform",
            isSelected ? "text-accent" : "text-muted-foreground/50"
          )}
        />
      </div>
    </button>
  );
}

// ============================================
// Agent Detail Panel Component
// ============================================

interface AgentDetailPanelProps {
  agent: AgentProfile;
  onRegister?: () => void;
  onOpenInCommandAura?: () => void;
  onInviteToDecisionRoom?: () => void;
}

function AgentDetailPanel({
  agent,
  onRegister,
  onOpenInCommandAura,
  onInviteToDecisionRoom,
}: AgentDetailPanelProps) {
  const KindIcon = getKindIcon(agent.identity.kind);
  const statusBadge = getStatusBadge(agent.registrationStatus);
  const StatusIcon = statusBadge.icon;
  const isDraft = agent.registrationStatus !== "live";

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-border/50">
        <div className="flex items-start gap-4">
          <div
            className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              agent.registrationStatus === "live"
                ? "bg-accent/20 text-accent"
                : "bg-muted/30 text-muted-foreground"
            )}
          >
            <KindIcon className="w-6 h-6" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">
                {agent.identity.displayName}
              </h2>
              <span
                className={cn(
                  "flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border",
                  statusBadge.className
                )}
              >
                <StatusIcon className="w-3 h-3" />
                {statusBadge.label}
              </span>
            </div>

            <p className="text-sm text-muted-foreground mt-1">
              {getKindLabel(agent.identity.kind)} · {getRoleLabel(agent.capability.role)}
            </p>
          </div>
        </div>

        {/* Draft Warning */}
        {isDraft && (
          <div className="mt-4 p-3 rounded-lg bg-warning/10 border border-warning/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-warning">
                  {agent.registrationStatus === "draft"
                    ? "Draft Agent — Not Registered Through Core"
                    : agent.registrationStatus === "pending-core-registration"
                    ? "Pending Core Registration"
                    : agent.registrationStatus === "requires-core-handler"
                    ? "Requires Core Handler"
                    : "Agent Unavailable"}
                </p>
                <p className="text-[10px] text-warning/80 mt-0.5">
                  {agent.registrationStatus === "draft"
                    ? "This agent cannot execute until registered through Core."
                    : agent.registrationStatus === "pending-core-registration"
                    ? "Registration request submitted. Awaiting Core approval."
                    : agent.registrationStatus === "requires-core-handler"
                    ? "A Core handler must be implemented before this agent can be activated."
                    : "This agent is currently unavailable for operational use."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          {isDraft && (
            <Button
              size="sm"
              className="h-8 text-xs bg-accent text-background hover:bg-accent/90 cursor-pointer"
              onClick={onRegister}
            >
              <Zap className="w-3 h-3 mr-1.5" />
              Register Through Core
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs border-border hover:bg-muted/50 hover:text-foreground cursor-pointer"
            onClick={onOpenInCommandAura}
          >
            Open in Command AURA
            {isDraft && <span className="ml-1 opacity-60">(Draft)</span>}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs border-border hover:bg-muted/50 hover:text-foreground cursor-pointer"
            onClick={onInviteToDecisionRoom}
          >
            Invite to Decision Room
            {isDraft && <span className="ml-1 opacity-60">(Draft)</span>}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Capability */}
        <Section title="Capability">
          <div className="space-y-3">
            <div>
              <Label>Strengths</Label>
              <ul className="mt-1 space-y-1">
                {agent.capability.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                    <CheckCircle2 className="w-3 h-3 text-success shrink-0 mt-0.5" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <Label>Limitations</Label>
              <ul className="mt-1 space-y-1">
                {agent.capability.limitations.map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                    <AlertTriangle className="w-3 h-3 text-warning shrink-0 mt-0.5" />
                    {l}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <Label>Allowed Surfaces</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {agent.capability.allowedSurfaces.map((surface) => (
                  <span
                    key={surface}
                    className="text-[10px] px-2 py-0.5 rounded bg-muted/20 text-muted-foreground border border-border/50"
                  >
                    {getSurfaceLabel(surface)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Intelligence */}
        <Section title="Intelligence / Provider">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Provider</Label>
              <p className="text-sm text-foreground capitalize mt-0.5">
                {agent.intelligence.providerType.replace("-", " ")}
              </p>
            </div>
            {agent.intelligence.modelLabel && (
              <div>
                <Label>Model</Label>
                <p className="text-sm text-foreground mt-0.5">
                  {agent.intelligence.modelLabel}
                </p>
              </div>
            )}
            <div>
              <Label>Status</Label>
              <p
                className={cn(
                  "text-sm mt-0.5 capitalize",
                  agent.intelligence.configStatus === "configured"
                    ? "text-success"
                    : agent.intelligence.configStatus === "draft"
                    ? "text-muted-foreground"
                    : "text-destructive"
                )}
              >
                {agent.intelligence.configStatus}
              </p>
            </div>
          </div>
        </Section>

        {/* Authority */}
        <Section title="Authority Policy">
          <div className="space-y-3">
            <div>
              <Label>Allowed Actions</Label>
              <ul className="mt-1 space-y-1">
                {agent.authority.allowedActions.map((action, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-success" />
                    {action}
                  </li>
                ))}
              </ul>
            </div>

            {agent.authority.blockedActions.length > 0 && (
              <div>
                <Label>Blocked Actions</Label>
                <ul className="mt-1 space-y-1">
                  {agent.authority.blockedActions.map((action, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-destructive" />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <Label>Approval Required For</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {agent.authority.approvalRequiredFor.externalSystems && (
                  <ApprovalBadge label="External Systems" />
                )}
                {agent.authority.approvalRequiredFor.secrets && (
                  <ApprovalBadge label="Secrets" />
                )}
                {agent.authority.approvalRequiredFor.productionImpact && (
                  <ApprovalBadge label="Production Impact" />
                )}
                {agent.authority.approvalRequiredFor.connectorWrites && (
                  <ApprovalBadge label="Connector Writes" />
                )}
                {agent.authority.approvalRequiredFor.costThreshold && (
                  <ApprovalBadge
                    label={`Cost > $${agent.authority.approvalRequiredFor.costThreshold}`}
                  />
                )}
              </div>
            </div>

            {agent.authority.auditorCounterpart && (
              <div>
                <Label>Auditor Counterpart</Label>
                <p className="text-sm text-foreground mt-0.5">
                  {agent.authority.auditorCounterpart}
                </p>
              </div>
            )}
          </div>
        </Section>

        {/* Memory */}
        <Section title="Memory Configuration">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scope</Label>
              <p className="text-sm text-foreground mt-0.5">
                {getMemoryScopeLabel(agent.memory.scope)}
              </p>
            </div>
            {agent.memory.retentionDays && (
              <div>
                <Label>Retention</Label>
                <p className="text-sm text-foreground mt-0.5">
                  {agent.memory.retentionDays} days
                </p>
              </div>
            )}
          </div>
        </Section>

        {/* Tools */}
        {agent.tools.length > 0 && (
          <Section title="Tools / Connectors">
            <div className="space-y-2">
              {agent.tools.map((tool, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex items-center justify-between p-2 rounded border",
                    tool.enabled
                      ? "bg-muted/10 border-border/50"
                      : "bg-muted/5 border-border/30 opacity-60"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Database className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-foreground">{tool.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {tool.requiresCoreApproval && (
                      <Lock className="w-3 h-3 text-warning" />
                    )}
                    <span
                      className={cn(
                        "text-[10px] px-1.5 py-0.5 rounded",
                        tool.enabled
                          ? "bg-success/20 text-success"
                          : "bg-muted/20 text-muted-foreground"
                      )}
                    >
                      {tool.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Timestamps */}
        <Section title="Metadata">
          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
            <div>
              <Label>Created</Label>
              <p className="mt-0.5">{new Date(agent.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <Label>Updated</Label>
              <p className="mt-0.5">{new Date(agent.updatedAt).toLocaleDateString()}</p>
            </div>
            {agent.registeredAt && (
              <div>
                <Label>Registered</Label>
                <p className="mt-0.5">
                  {new Date(agent.registeredAt).toLocaleDateString()}
                </p>
              </div>
            )}
            {agent.lastActiveAt && (
              <div>
                <Label>Last Active</Label>
                <p className="mt-0.5">
                  {new Date(agent.lastActiveAt).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

// ============================================
// Empty State Component
// ============================================

function EmptyAgentSelection() {
  return (
  <div className="flex-1 flex items-center justify-center p-8 min-h-[400px]">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-muted/20 border border-border/50 flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-muted-foreground/50" />
        </div>
        <h3 className="text-sm font-medium text-foreground mb-2">
          Select an Agent
        </h3>
        <p className="text-xs text-muted-foreground">
          Choose an agent from the registry to view their profile, capabilities,
          authority policy, and tools.
        </p>
      </div>
    </div>
  );
}

function EmptyAgentList({ onCreateAgent }: { onCreateAgent?: () => void }) {
  return (
    <div className="p-6 text-center">
      <div className="w-12 h-12 rounded-xl bg-muted/20 border border-border/50 flex items-center justify-center mx-auto mb-3">
        <Bot className="w-6 h-6 text-muted-foreground/50" />
      </div>
      <h3 className="text-sm font-medium text-foreground mb-1">
        No Registered Agents from Core
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Create a draft agent to prepare registration.
      </p>
      <Button
          size="sm"
          className="h-8 text-xs bg-accent text-background hover:bg-accent/90 cursor-pointer"
          onClick={onCreateAgent}
        >
          <Plus className="w-3 h-3 mr-1.5" />
          Create Draft Agent
        </Button>
    </div>
  );
}

function CreateAgentDialog({
  open,
  onClose,
  onCreateAgent,
}: {
  open: boolean;
  onClose: () => void;
  onCreateAgent?: (request: AdminAgentCreateInput) => Promise<void> | void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [kind, setKind] = useState<AgentKind>("ai");
  const [domain, setDomain] = useState<AgentDomain>("climb-one");
  const [role, setRole] = useState<AgentRole>("advisor");
  const [providerType, setProviderType] = useState<AgentProviderType>("not-configured");
  const [modelLabel, setModelLabel] = useState("");
  const [memoryScope, setMemoryScope] = useState<MemoryScope>("workspace");
  const [strengths, setStrengths] = useState("Operational reasoning\nDecision support");
  const [limitations, setLimitations] = useState("Requires Core governance before execution");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!displayName.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onCreateAgent?.({
        displayName: displayName.trim(),
        kind,
        domain,
        role,
        providerType,
        modelLabel: modelLabel.trim() || undefined,
        memoryScope,
        strengths: splitLines(strengths),
        limitations: splitLines(limitations),
      });
      setDisplayName("");
      setModelLabel("");
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/70 backdrop-blur-sm">
      <form
        onSubmit={submit}
        className="w-[560px] rounded-2xl border border-border/60 bg-panel p-5 shadow-2xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-foreground">
              Create Agent Through Core
            </h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Creates a governed draft profile. Execution stays blocked until Core registration and authority policy allow it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted/30 hover:text-foreground"
          >
            Close
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Display Name">
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              className={inputClass}
              placeholder="SAP Integration Reviewer"
            />
          </Field>
          <Field label="Kind">
            <select value={kind} onChange={(event) => setKind(event.target.value as AgentKind)} className={inputClass}>
              {["ai", "human", "external-consultant", "internal-worker", "auditor", "connector-operator", "domain-expert"].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </Field>
          <Field label="Domain">
            <select value={domain} onChange={(event) => setDomain(event.target.value as AgentDomain)} className={inputClass}>
              {["climb-one", "sap", "aws", "browser", "desktop", "api", "integration", "data", "governance", "custom"].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </Field>
          <Field label="Role">
            <select value={role} onChange={(event) => setRole(event.target.value as AgentRole)} className={inputClass}>
              {["executor", "reviewer", "auditor", "advisor", "supervisor", "connector-operator", "memory-curator", "domain-expert"].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </Field>
          <Field label="Provider">
            <select value={providerType} onChange={(event) => setProviderType(event.target.value as AgentProviderType)} className={inputClass}>
              {["not-configured", "openai", "anthropic", "local", "human", "external-api"].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </Field>
          <Field label="Model Label">
            <input
              value={modelLabel}
              onChange={(event) => setModelLabel(event.target.value)}
              className={inputClass}
              placeholder="Provider adapter"
            />
          </Field>
          <Field label="Memory Scope">
            <select value={memoryScope} onChange={(event) => setMemoryScope(event.target.value as MemoryScope)} className={inputClass}>
              {["none", "session", "workspace", "tenant", "domain-pack", "canonical-review-required"].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <Field label="Strengths">
            <textarea value={strengths} onChange={(event) => setStrengths(event.target.value)} className={textareaClass} />
          </Field>
          <Field label="Limitations">
            <textarea value={limitations} onChange={(event) => setLimitations(event.target.value)} className={textareaClass} />
          </Field>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!displayName.trim() || submitting}
            className="bg-accent text-background hover:bg-accent/90"
          >
            {submitting ? "Creating..." : "Create Draft Agent"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function splitLines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

// ============================================
// Helper Components
// ============================================

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
      {children}
    </span>
  );
}

function ApprovalBadge({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-warning/10 text-warning border border-warning/20">
      <Lock className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

// ============================================
// Main Component
// ============================================

export function AgentStudio({
  agents,
  selectedAgentId,
  filterStatus,
  filterKind,
  onSelectAgent,
  onFilterStatus,
  onFilterKind,
  onCreateAgent,
  onRegisterAgent,
  onOpenInCommandAura,
  onInviteToDecisionRoom,
}: AgentStudioProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  // Filter agents
  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      // Status filter
      if (filterStatus !== "all" && agent.registrationStatus !== filterStatus) {
        return false;
      }

      // Kind filter
      if (filterKind !== "all" && agent.identity.kind !== filterKind) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          agent.identity.displayName.toLowerCase().includes(query) ||
          agent.identity.domain.toLowerCase().includes(query) ||
          agent.capability.role.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [agents, filterStatus, filterKind, searchQuery]);

  const selectedAgent = useMemo(() => {
    if (!selectedAgentId) return null;
    return agents.find((a) => a.identity.id === selectedAgentId) || null;
  }, [agents, selectedAgentId]);

  return (
    <div className="relative flex-1 flex overflow-hidden">
      <CreateAgentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreateAgent={onCreateAgent}
      />
      {/* Left Panel - Agent Registry */}
      <div className="w-[320px] border-r border-border/50 flex flex-col bg-panel/30">
        {/* Header */}
        <div className="p-4 border-b border-border/50">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-foreground">
              Agent Registry
            </h2>
        <Button
          size="sm"
          className="h-7 text-xs bg-accent text-background hover:bg-accent/90 cursor-pointer"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="w-3 h-3 mr-1" />
          Create
        </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search agents..."
              className="w-full h-8 pl-9 pr-3 text-xs bg-muted/20 border border-border/50 rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent/50"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="p-3 border-b border-border/50">
          <FilterTabs
            filterStatus={filterStatus}
            filterKind={filterKind}
            onFilterStatus={onFilterStatus}
            onFilterKind={onFilterKind}
            agents={agents}
          />
        </div>

        {/* Agent List */}
        <div className="flex-1 overflow-y-auto p-3">
          {filteredAgents.length === 0 ? (
            <EmptyAgentList onCreateAgent={() => setCreateOpen(true)} />
          ) : (
            <div className="space-y-2">
              {filteredAgents.map((agent) => (
                <AgentListItem
                  key={agent.identity.id}
                  agent={agent}
                  isSelected={selectedAgentId === agent.identity.id}
                  onSelect={() => onSelectAgent(agent.identity.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Center/Right Panel - Agent Detail */}
      <div className="flex-1 bg-panel/30 overflow-y-auto">
        {selectedAgent ? (
          <AgentDetailPanel
            agent={selectedAgent}
            onRegister={() => onRegisterAgent?.(selectedAgent.identity.id)}
            onOpenInCommandAura={() =>
              onOpenInCommandAura?.(selectedAgent.identity.id)
            }
            onInviteToDecisionRoom={() =>
              onInviteToDecisionRoom?.(selectedAgent.identity.id)
            }
          />
        ) : (
          <EmptyAgentSelection />
        )}
      </div>
    </div>
  );
}
